import _ from 'lodash';
import Feedback from 'feedback-lite';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Nav, Navbar, NavDropdown } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import toastr from './toastr';
import config from '../config';
import BurgerMenuButton from './nav/BurgerMenuButton';
import NavNotifications from './nav/NavNotifications';
import NavChat from './nav/NavChat';
import Api from '../services/ApiService';
import socket from '../services/SocketService';
import browserHistory from '../history';
import { logout, removeNote, addNote } from '../actions/AppActions';
import MainSearch from './search/MainSearch';
import Avatar from './user/Avatar';
import AuthService from '../services/AuthService';
import { modal } from './modal/ModalEmitter';

class BlazeNavbar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      useLogo: true,
      showMobileSearch: false,
    };
  }

  componentDidMount() {
    this.loadHtml2Canvas().then(() => {
      this.feedback = new Feedback({
        onSubmit: data => {
          data.pageName = this.props.page.pageName;
          Api.post('/feedback', data)
            .then(response => {
              const { ticketId } = response.data;
              modal.alert({
                title: 'Support Ticket Submitted',
                message: (
                  <div>
                    <h4>Ticket #{ticketId}</h4>
                    <p>We have received your support request and routed it to our helpdesk.</p>

                    <a href={`https://portal.dsisolutions.biz/helpdesk/tickets/${ticketId}`}>
                      Track Ticket <i className="fas fa-external-link-alt" />
                    </a>
                  </div>
                ),
              });
              toastr.success('Feedback submitted');
            })
            .catch(() => {
              toastr.error('Failed to create support ticket');
            });
        },
        html2canvas: this.html2canvas,
        includeHtml: false, // too much data
      });
    });

    socket.notifications.on('addNote', payload => {
      this.props.addNote(payload);
    });

    socket.notifications.on('note.remove', id => {
      // skip the action
      this.props.dispatch({
        type: 'REMOVE_NOTIFICATION',
        payload: {
          data: {
            id,
          },
        },
      });
    });
  }

  getBranchName = () => {
    return _.get(this.props.user, 'branch.name', 'Eastern Service Center');
  };

  loadHtml2Canvas() {
    if (this.html2canvas) {
      return Promise.resolve();
    }

    return import('html2canvas').then(mod => {
      this.html2canvas = mod.default ? mod.default : mod;
      return Promise.resolve();
    });
  }

  navigateToLogin() {
    browserHistory.push('/login');
  }

  navigateToProfile() {
    browserHistory.push('/profile');
  }

  switchBranch(branchId) {
    AuthService.switchBranch(branchId).then(() => {
      location.reload(false);
    });
  }

  handleMobileSearchClick = () => {
    const showMobileSearch = !this.state.showMobileSearch;

    this.setState({ showMobileSearch }, () => {
      if (showMobileSearch) {
        window.document.querySelector('#main-search').focus();
      }
    });
  };

  handleBurgerClick() {
    const element = document.getElementById('menu-bar');
    if (element) {
      const { classList } = element;

      if (classList.contains('mini')) {
        classList.remove('mini');
        return;
      }

      classList.add('mini');
    }
  }

  renderBranch() {
    const branch = this.getBranchName();
    const logo =
      this.state.useLogo && this.props.branch.logo ? (
        <span className="logo mr-2">
          <img
            src={`${config.apiUrl}/logo?f=${this.props.branch.logo}`}
            onError={() => {
              this.setState({ useLogo: false });
            }}
            alt="Company Logo"
            width="50"
          />
        </span>
      ) : (
        <span className="logo-placeholder">{branch.charAt(0)}</span>
      );

    return (
      <Navbar.Brand href="#" onClick={() => browserHistory.push('/')}>
        {logo}
        <span className="branch-name">{branch}</span>
      </Navbar.Brand>
    );
  }

  renderFeedback = () => {
    if (this.feedback) {
      this.feedback.mount();
    }
  };

  renderBranches() {
    const { user, branchOptions } = this.props;

    if (!user.branches || user.branches.length === 0) {
      return;
    }

    // user.branches contains only companyIds
    // user.companies maps a display name to a companyId
    // numerical id orders do not imply lexicographical orders - must sort

    // keyed on company names for optgroups
    const renderArray = [];

    // render companies in sorted order
    Object.keys(branchOptions)
      .sort()
      .forEach((companyName, key) => {
        // render branches within each company in sorted order
        branchOptions[companyName].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        // sort by "sortNum" associated with branch
        branchOptions[companyName].sort((a, b) => {
          if (a.sortNum === b.sortNum) {
            return 0;
          }

          if (a.sortNum === 0) {
            return 1;
          }

          if (b.sortNum === 0) {
            return -1;
          }

          return a.sortNum > b.sortNum ? 1 : -1;
        });

        renderArray.push(<NavDropdown.Header key={key}>{companyName}</NavDropdown.Header>);

        for (const branch of branchOptions[companyName]) {
          renderArray.push(
            <NavDropdown.Item
              key={`${key}-${branch.id}`}
              active={branch.active}
              onClick={() => this.switchBranch(branch.id)}
            >
              {branch.name}
            </NavDropdown.Item>
          );
        }
      });

    renderArray.push(<NavDropdown.Divider key="d" />);
    return renderArray;
  }

  renderLoggedInUser() {
    const { user } = this.props;

    if (!user.id) {
      return <Nav.Link onClick={this.navigateToLogin}>Login</Nav.Link>;
    }

    const avatar = (
      <div className="user-badge">
        <Avatar user={user} width={35} />
        <span className="username d-none d-lg-inline">{this.props.user.displayName || this.props.user.username}</span>
      </div>
    );

    return (
      <NavDropdown
        id="user-menu-dropdown"
        title={avatar}
        alignRight
        className="top-level-menu no-caret d-none d-sm-block"
      >
        {this.renderBranches()}
        <NavDropdown.Item active={false} onClick={this.navigateToProfile}>
          Profile
        </NavDropdown.Item>
        {this.props.config.features.createSupportTicket && (
          <NavDropdown.Item active={false} onClick={this.renderFeedback}>
            Create Support Ticket
          </NavDropdown.Item>
        )}
        <NavDropdown.Item active={false} onClick={this.props.logout}>
          Logout
        </NavDropdown.Item>
      </NavDropdown>
    );
  }

  render() {
    const { notifications, enableChat } = this.props;

    const className = classNames({
      'mobile-search-focused': this.state.showMobileSearch,
    });

    return (
      <Navbar className={className}>
        <div className="navbar-left">
          <Nav className="navbar-header">
            <BurgerMenuButton />
            {this.renderBranch()}
          </Nav>
          <Nav className="navbar-form">
            <MainSearch />
          </Nav>
        </div>

        <Nav className="nav-toolbar">
          {enableChat && <NavChat />}
          <NavNotifications notifications={notifications} removeNote={this.props.removeNote} />
          {this.renderLoggedInUser()}
        </Nav>
        <div className="mobile-search" onClick={this.handleMobileSearchClick}>
          {!this.state.showMobileSearch ? <i className="fas fa-search" /> : <i className="fas fa-times" />}
        </div>
      </Navbar>
    );
  }
}

BlazeNavbar.propTypes = {
  addNote: PropTypes.func,
  branch: PropTypes.object,
  branchOptions: PropTypes.object,
  config: PropTypes.object,
  enableChat: PropTypes.bool,
  logout: PropTypes.func,
  notifications: PropTypes.array,
  page: PropTypes.object,
  removeNote: PropTypes.func,
  user: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
  return { ...bindActionCreators({ logout, removeNote, addNote }, dispatch), dispatch };
}

function mapStateToProps(state) {
  return {
    page: state.page,
    user: state.app.user,
    branch: state.app.branch,
    branchOptions: state.app.branchOptions,
    config: state.app.config,
    notifications: state.app.notifications,
    enableChat: _.get(state.app.config, 'features.chat', true),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BlazeNavbar);
